import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/firebase';
import { 
  collection, 
  getDocs, 
  query, 
  where,
  doc,
  getDoc,
  updateDoc,
  orderBy as firestoreOrderBy,
  limit as firestoreLimit,
  DocumentData,
  writeBatch,
  QueryConstraint
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { applyInventoryMovement } from '../lib/inventoryService';
import { usePermissions } from './usePermissions';
import { 
  Inventory, 
  InventoryMovement, 
  InventoryMovementType, 
  CreateInventoryMovementData,
  InventoryQueryParams,
  InventoryReport
} from '../types/warehouse';

export const useInventory = () => {
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const orgId = (currentUser as any)?.organizationId as string | undefined;
  const { hasAccess } = usePermissions();

  // Készletadatok betöltése
  const fetchInventory = useCallback(async (params?: InventoryQueryParams) => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('inventory')) {
      setLoading(false);
      return [];
    }

    setLoading(true);
    try {
      const inventoryRef = collection(db, 'inventory');
      const constraints: QueryConstraint[] = [];
      if (!orgId) { setLoading(false); return []; }
      constraints.push(where('organizationId', '==', orgId));
      
      // Szűrési feltételek hozzáadása
      if (params?.productId) {
        constraints.push(where('productId', '==', params.productId));
      }
      
      if (params?.productCode) {
        constraints.push(where('productCode', '==', params.productCode));
      }
      
      if (params?.warehouseId) {
        constraints.push(where('warehouseId', '==', params.warehouseId));
      }
      
      if (params?.locationId) {
        constraints.push(where('locationId', '==', params.locationId));
      }
      
      const q = constraints.length > 0 
        ? query(inventoryRef, ...constraints)
        : query(inventoryRef);
        
      const snapshot = await getDocs(q);
      
      let inventoryItems = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as Inventory[];
      
      // További szűrések, amelyeket Firestore-ban nem tudunk közvetlenül kezelni
      if (params?.belowMinimum) {
        inventoryItems = inventoryItems.filter(
          item => item.minQuantity !== undefined && item.quantity < item.minQuantity
        );
      }
      
      if (params?.aboveMaximum) {
        inventoryItems = inventoryItems.filter(
          item => item.maxQuantity !== undefined && item.quantity > item.maxQuantity
        );
      }
      
      if (params?.onlyAvailable) {
        inventoryItems = inventoryItems.filter(item => item.quantity > 0);
      }
      
      if (params?.searchTerm) {
        const term = params.searchTerm.toLowerCase();
        inventoryItems = inventoryItems.filter(item => 
          item.productName.toLowerCase().includes(term) || 
          item.productCode.toLowerCase().includes(term)
        );
      }
      
      setInventory(inventoryItems);
      setError(null);
      return inventoryItems;
    } catch (err) {
      console.error('Hiba a készletadatok betöltésekor:', err);
      setError('Nem sikerült betölteni a készletadatokat.');
      return [];
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Készletmozgások betöltése
  const fetchMovements = useCallback(async (params: {
    productId?: string;
    warehouseId?: string;
    fromDate?: Date;
    toDate?: Date;
    type?: InventoryMovementType;
    limit?: number;
    lastDoc?: DocumentData | null;
    pageSize?: number;
    sortOrder?: 'asc' | 'desc';
  } = {}) => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('inventory')) {
      setLoading(false);
      return {
        movements: [],
        lastDoc: null,
        totalCount: 0
      };
    }

    setLoading(true);
    try {
      const movementsRef = collection(db, 'inventoryMovements');
      const constraints: QueryConstraint[] = [];
      if (!orgId) { setLoading(false); return { movements: [], lastDoc: null, totalCount: 0 }; }
      constraints.push(where('organizationId', '==', orgId));
      
      if (params.productId) {
        constraints.push(where('productId', '==', params.productId));
      }
      
      if (params.warehouseId) {
        constraints.push(where('warehouseId', '==', params.warehouseId));
      }
      
      if (params.type) {
        constraints.push(where('type', '==', params.type));
      }
      
      if (params.fromDate) {
        constraints.push(where('date', '>=', params.fromDate));
      }
      
      if (params.toDate) {
        constraints.push(where('date', '<=', params.toDate));
      }
      
      // Rendezés dátum szerint a megadott irány alapján (alapértelmezetten csökkenő)
      constraints.push(firestoreOrderBy('date', params.sortOrder || 'desc'));
      
      // Lapozás - vagy limit vagy pageSize használata
      const pageSize = params.pageSize || 100; // Alapértelmezett oldál méret
      constraints.push(firestoreLimit(params.limit || pageSize));
      
      // Ha van lastDoc, akkor attól kezdve kérjük le az adatokat (lapozás)
      let q = query(movementsRef, ...constraints);
      
      // Adatok lekérése
      const snapshot = await getDocs(q);
      
      const movementsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as InventoryMovement[];
      
      // Utolsó dokumentum mentése lapozáshoz
      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      
      // Ha első lap, akkor beállítjuk a teljes találatok számát
      // (Megjegyzés: Firestore-ban nincs egyszerű módja a pontos COUNT lekérdezésnek,
      // ezért ez csak egy közelítés)
      let totalCount = 0;
      if (!params.lastDoc) {
        // Ha van kevesebb elem mint a pageSize, akkor pontosan tudjuk a számot
        if (snapshot.size < pageSize) {
          totalCount = snapshot.size;
        } else {
          // Ha tele van az oldal, akkor becsüljük 3-5x-re a pageSize-t
          // Ez reálisabb becslés mint a 1000x
          totalCount = pageSize * 5; // Így 20 * 5 = 100 helyett 20,000
        }
      }
      
      setMovements(movementsList);
      setError(null);
      
      // Visszaadjuk a mozgásokat, az utolsó dokumentumot és a becsült teljes számot
      return {
        movements: movementsList,
        lastDoc: lastVisible,
        totalCount
      };
    } catch (err) {
      console.error('Hiba a készletmozgások betöltésekor:', err);
      setError('Nem sikerült betölteni a készletmozgásokat.');
      return {
        movements: [],
        lastDoc: null,
        totalCount: 0
      };
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Készletmozgás létrehozása
  const createInventoryMovement = useCallback(async (data: CreateInventoryMovementData): Promise<InventoryMovement> => {
    if (!currentUser) throw new Error('A felhasználó nincs bejelentkezve.');
    return applyInventoryMovement(data, { uid: currentUser.uid, email: currentUser.email, organizationId: orgId });
  }, [currentUser, orgId]);

  /** Karbantartás: a termékek currentStock mezőjének újraszámolása a készletsorokból (ha valaha elcsúszott). */
  const recalculateProductTotals = useCallback(async (): Promise<number> => {
    if (!orgId) return 0;
    const snap = await getDocs(query(collection(db, 'inventory'), where('organizationId', '==', orgId)));
    const totals = new Map<string, number>();
    snap.docs.forEach(d => { const x = d.data() as any; totals.set(x.productId, (totals.get(x.productId) || 0) + Number(x.quantity || 0)); });
    let n = 0;
    let batch = writeBatch(db);
    for (const [pid, total] of Array.from(totals.entries())) {
      batch.update(doc(db, 'products', pid), { currentStock: Math.round(total * 1000) / 1000, updatedAt: new Date() });
      n++;
      if (n % 400 === 0) { await batch.commit(); batch = writeBatch(db); }
    }
    await batch.commit();
    return n;
  }, [orgId]);

  // Készletjelentés lekérése
  const getInventoryReport = useCallback(async (productIds?: string[]): Promise<InventoryReport[]> => {
    try {
      if (productIds && productIds.length > 0) {
        // FireStore in query korlátozott 10 elemre, ezért több lekérdezésre kell bontani
        const chunkSize = 10;
        const chunks = [];
        
        for (let i = 0; i < productIds.length; i += chunkSize) {
          chunks.push(productIds.slice(i, i + chunkSize));
        }
        
        const results: Inventory[] = [];
        
        for (const chunk of chunks) {
          const inventoryRef = collection(db, 'inventory');
          const q = orgId
            ? query(inventoryRef, where('organizationId', '==', orgId), where('productId', 'in', chunk))
            : query(inventoryRef, where('productId', 'in', chunk));
          const snapshot = await getDocs(q);
          const items = snapshot.docs.map((doc) => doc.data() as Inventory);
          results.push(...items);
        }
        
        return processInventoryReport(results);
      } else {
        const inventoryRef = collection(db, 'inventory');
        const snapshot = await getDocs(orgId ? query(inventoryRef, where('organizationId', '==', orgId)) : inventoryRef);
        const items = snapshot.docs.map((doc) => doc.data() as Inventory);
        return processInventoryReport(items);
      }
    } catch (err) {
      console.error('Hiba a készletjelentés lekérésekor:', err);
      throw new Error('Nem sikerült lekérni a készletjelentést.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  
  // Készletjelentés feldolgozása
  const processInventoryReport = (inventoryItems: Inventory[]): InventoryReport[] => {
    const productMap = new Map<string, InventoryReport>();
    
    // Csoportosítás termékek szerint
    inventoryItems.forEach(item => {
      if (!productMap.has(item.productId)) {
        productMap.set(item.productId, {
          productId: item.productId,
          productCode: item.productCode,
          productName: item.productName,
          totalQuantity: 0,
          reservedQuantity: 0,
          availableQuantity: 0,
          unit: item.unit,
          warehouseDetails: []
        });
      }
      
      const report = productMap.get(item.productId)!;
      const quantity = item.quantity || 0;
      const reservedQuantity = item.reservedQuantity || 0;
      const availableQuantity = Math.max(0, quantity - reservedQuantity);
      
      report.totalQuantity += quantity;
      report.reservedQuantity += reservedQuantity;
      report.availableQuantity += availableQuantity;
      
      report.warehouseDetails.push({
        warehouseId: item.warehouseId,
        warehouseName: item.warehouseName,
        quantity,
        reservedQuantity,
        availableQuantity
      });
    });
    
    return Array.from(productMap.values());
  };

  // Készletmozgás frissítése (főként dokumentumok hozzáadásához)
  const updateInventoryMovement = useCallback(async (
    movementId: string, 
    updates: Partial<InventoryMovement>
  ): Promise<void> => {
    if (!currentUser) {
      throw new Error('A felhasználó nincs bejelentkezve.');
    }
    
    try {
      const movementRef = doc(db, 'inventoryMovements', movementId);
      await updateDoc(movementRef, {
        ...updates,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      
      console.log('Készletmozgás sikeresen frissítve:', movementId);
    } catch (err) {
      console.error('Hiba a készletmozgás frissítésekor:', err);
      throw new Error('Nem sikerült frissíteni a készletmozgást.');
    }
  }, [currentUser]);

  // Dokumentum eltávolítása készletmozgásból
  const removeDocumentFromMovement = useCallback(async (
    movementId: string,
    documentUrl: string
  ): Promise<void> => {
    if (!currentUser) {
      throw new Error('A felhasználó nincs bejelentkezve.');
    }
    
    try {
      // Először lekérjük a jelenlegi mozgást
      const movementRef = doc(db, 'inventoryMovements', movementId);
      const movementDoc = await getDoc(movementRef);
      
      if (!movementDoc.exists()) {
        throw new Error('A készletmozgás nem található.');
      }
      
      const movementData = movementDoc.data() as InventoryMovement;
      const currentAttachments = movementData.attachments || [];
      
      // Eltávolítjuk a megadott URL-t
      const updatedAttachments = currentAttachments.filter(url => url !== documentUrl);
      
      // Frissítjük a dokumentumot
      await updateDoc(movementRef, {
        attachments: updatedAttachments,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      
      console.log('Dokumentum sikeresen eltávolítva:', documentUrl);
    } catch (err) {
      console.error('Hiba a dokumentum eltávolításakor:', err);
      throw new Error('Nem sikerült eltávolítani a dokumentumot.');
    }
  }, [currentUser]);

  return {
    inventory,
    movements,
    loading,
    error,
    fetchInventory,
    fetchMovements,
    createInventoryMovement,
    recalculateProductTotals,
    getInventoryReport,
    updateInventoryMovement,
    removeDocumentFromMovement
  };
}; 